import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FIELD_VALUES, InputType, OutputType } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { InterestTypeExpiryFunnelView } from '@funnels/ui-components/Questions/InterestTypeExpiry';
import { run } from '@funnels/utils';

import { ClickableText } from '../../../../Layouts/_common/style';
import {
  CurrentInterestRateEditableView,
  InterestOnlyPeriodEditableView,
  InterestTypeEditableView,
  InterestTypeTermEditableView,
  MinimumRepaymentsEditableView,
  RemainingLoanTermEditableView,
  RepaymentTypeDropdownEditableView,
} from '../../../../Questions';

interface InputToggleProps {
  value: InputType;
  onClick: () => void;
}

function InputToggle(props: InputToggleProps) {
  const { tracking } = useAnalytics();
  const output = OutputType[props.value];
  const label = `Not sure? Enter your ${output} instead`;

  return (
    <ClickableText
      onClick={run(props.onClick, () => tracking?.button.link({ text: label, position: 'Property - Current Loan' }))}
    >
      {label}.
    </ClickableText>
  );
}

export function CurrentLoanDetailsFields(props: {
  input1: InputType;
  input2: InputType;
  output: InputType;
  setInput1: (input: InputType) => void;
  setInput2: (input: InputType) => void;
  repaymentType: string;
  interestType: string;
  liabilityIndex: number;
}) {
  const { siphonFixedRateExpRelease } = useFlags();

  const { input1, input2, output, setInput1, setInput2, repaymentType, interestType, liabilityIndex } = props;

  function getInput(input: InputType) {
    switch (input) {
      case InputType.INTEREST_RATE:
        return <CurrentInterestRateEditableView liabilityIndex={liabilityIndex} label="Your current interest rate" />;

      case InputType.REMAINING_TERM:
        return <RemainingLoanTermEditableView liabilityIndex={liabilityIndex} hasOneMonthMinimum />;

      case InputType.MINIMUM_REPAYMENTS:
      default:
        return <MinimumRepaymentsEditableView liabilityIndex={liabilityIndex} />;
    }
  }

  return (
    <>
      <RepaymentTypeDropdownEditableView liabilityIndex={liabilityIndex} />
      <InterestTypeEditableView liabilityIndex={liabilityIndex} />
      {interestType === FIELD_VALUES.FIXED_RATE && !siphonFixedRateExpRelease && (
        <InterestTypeTermEditableView liabilityIndex={liabilityIndex} />
      )}
      {interestType === FIELD_VALUES.FIXED_RATE && siphonFixedRateExpRelease && (
        <InterestTypeExpiryFunnelView liabilityIndex={liabilityIndex} label={'Fixed term expiry date'} />
      )}

      {repaymentType === FIELD_VALUES.INTEREST_ONLY && (
        <InterestOnlyPeriodEditableView liabilityIndex={liabilityIndex} />
      )}
      {getInput(input1)}
      {repaymentType !== FIELD_VALUES.INTEREST_ONLY && <InputToggle value={output} onClick={() => setInput1(output)} />}
      {getInput(input2)}
      <InputToggle value={output} onClick={() => setInput2(output)} />
    </>
  );
}
